// Shared recharts area chart for Vella analytics. Gradient fill, horizontal
// gridlines only, clean axes, dot tooltip, legend for multi-series.
// Kept in its own file so it doesn't collide with the legacy local `AreaChart`
// helper in App.jsx (recharts also exports an `AreaChart`).
import React from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

// Vella palette
const OCEAN = "#275c73"; // primary series
const GREEN = "#3f8a63"; // secondary (revenue)

const moneyFmt = (v) =>
  typeof v === "number"
    ? v >= 1000 ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `$${v}`
    : v;

/**
 * @param {Array<object>} data   rows, e.g. [{ label: 'Mon', spend: 120, revenue: 460 }]
 * @param {string} xKey          row key for the X axis (default 'label')
 * @param {Array<{key,color,name}>} series  one entry per Area
 * @param {number} height        chart height in px (default 300)
 * @param {boolean} money        format Y axis + tooltip as currency
 */
export default function VellaAreaChart({ data = [], xKey = "label", series = [], height = 300, money = false }) {
  const tickFmt = money ? moneyFmt : undefined;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs>
          {series.map((s) => (
            <linearGradient key={s.key} id={`vella-grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={s.color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={s.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey={xKey} axisLine={false} tickLine={false} tick={{ fill: "#9ba1a3", fontSize: 12 }} minTickGap={24} />
        <YAxis axisLine={false} tickLine={false} tick={{ fill: "#9ba1a3", fontSize: 12 }} tickFormatter={tickFmt} width={48} />
        <Tooltip
          formatter={money ? (v) => moneyFmt(v) : undefined}
          contentStyle={{ borderRadius: 10, border: "1px solid #dfe2e3", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: 12 }}
        />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {series.map((s) => (
          <Area
            key={s.key}
            type="natural"
            dataKey={s.key}
            stroke={s.color}
            fill={`url(#vella-grad-${s.key})`}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            name={s.name}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

export { OCEAN, GREEN };
